import { useSyncExternalStore } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Navigate, Outlet, useLocation } from "react-router-dom"

import { LoadingSection } from "@/components/loading-section"
import { Button } from "@/components/ui/button"
import { OnboardingPage } from "@/features/onboarding/pages/onboarding-page"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useInitSettings } from "@/hooks/use-init-settings"
import { isM365ToolEnabled } from "@/hooks/use-route-overrides"
import { useUserSettings } from "@/hooks/use-user-settings"
import { queryKeys } from "@/lib/query-keys"

import { OnboardingOverlayContext } from "../onboarding-overlay-context"
import {
  isConfiguredAdminPath,
  isGdprAccepted,
  readOnboardingCompletionReady,
  readSoftTrainingDismissed,
  resolveOnboardingStep,
  safeReturnTo,
  subscribeOnboardingCompletionReady,
  withOnboardingCompletedFlag,
  withoutForceOnboardingFlag,
} from "../onboarding-utils"

export function OnboardingCoordinator({ defaultRoute }: { defaultRoute: string }) {
  const { t } = useTranslation()
  const location = useLocation()
  const queryClient = useQueryClient()
  const completionReady = useSyncExternalStore(
    subscribeOnboardingCompletionReady,
    readOnboardingCompletionReady,
    readOnboardingCompletionReady
  )
  // Both queries authenticate with the token held by the settings singleton, so
  // firing them during auth bootstrap fails before any request leaves the page.
  const { isSettingsInitialized } = useInitSettings()
  const settingsQuery = useUserSettings({ enabled: isSettingsInitialized })
  const configQuery = useConfigQuery({ enabled: isSettingsInitialized })

  // Only a genuine failure gets the error card. A query that is merely waiting —
  // disabled, or paused because the browser is offline — keeps the loading state
  // so it can resolve itself once it is allowed to run.
  if (settingsQuery.isError || configQuery.isError) {
    return (
      <main className="bg-background flex min-h-svh items-center justify-center p-6">
        <section className="border-border bg-card w-full max-w-md rounded-xl border p-6 text-center shadow-sm">
          <h1 className="font-display text-2xl">
            {t("personalization.smoothOnboarding.loadErrorTitle")}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {t("personalization.smoothOnboarding.loadErrorDescription")}
          </p>
          <Button
            className="mt-6 min-h-10"
            onClick={() => {
              // `refetch` is a no-op for a disabled or paused query, so reset
              // both instead — that clears the error and refetches whichever one
              // is allowed to run.
              void queryClient.resetQueries({ queryKey: queryKeys.userSettings })
              void queryClient.resetQueries({ queryKey: queryKeys.config })
            }}
          >
            {t("personalization.smoothOnboarding.retry")}
          </Button>
        </section>
      </main>
    )
  }

  if (!settingsQuery.data || !configQuery.data) {
    return (
      <section className="flex min-h-svh items-center justify-center">
        <LoadingSection />
      </section>
    )
  }

  const data = settingsQuery.data.data
  const progress = data.onboarding_progress
  const params = new URLSearchParams(location.search)
  const showCompleted = params.get("completed") === "1" || completionReady
  const isOnboardingRoute = location.pathname === "/onboarding"
  const isTrainingRoute = location.pathname.startsWith("/training")
  const isOnboardingTrainingHandoff = isTrainingRoute && params.get("onboarding") === "1"
  const requestedPath = `${location.pathname}${location.search}${location.hash}`
  const returnTo = safeReturnTo(
    isOnboardingRoute
      ? params.get("returnTo") || progress?.return_to
      : progress?.return_to || withoutForceOnboardingFlag(requestedPath),
    defaultRoute
  )
  const forceRequested =
    configQuery.data.deploymentSlot === "test-2" && params.get("forceOnboarding") === "1"
  // Force-reset stays on /onboarding only until completion latches Ready, which
  // must overlay returnTo rather than a blank onboarding shell.
  if (forceRequested && !showCompleted) {
    if (isOnboardingRoute) return <Outlet />
    const forcedReturnTo = safeReturnTo(withoutForceOnboardingFlag(requestedPath), defaultRoute)
    return (
      <Navigate
        replace
        to={`/onboarding?forceOnboarding=1&returnTo=${encodeURIComponent(forcedReturnTo)}`}
      />
    )
  }
  const policyTarget = isOnboardingRoute || isTrainingRoute ? returnTo : requestedPath
  const adminExempt = isConfiguredAdminPath(configQuery.data.frontend.routes, policyTarget)
  const training = adminExempt
    ? { enabled: false, ai_awareness_completed: true, has_legacy_training: true }
    : configQuery.data.training_gating
  const resolution = resolveOnboardingStep({
    completedAt: data.onboarding_completed_at,
    progress,
    ui: data.ui,
    training,
    showCompleted,
    softTrainingDismissed: readSoftTrainingDismissed(),
    m365Enabled: isM365ToolEnabled(),
  })

  if (resolution.step === null) {
    return isOnboardingRoute || isOnboardingTrainingHandoff ? (
      <Navigate replace to={returnTo} />
    ) : (
      <Outlet />
    )
  }

  if (isOnboardingRoute) {
    const destination =
      params.get("completed") === "1" ? withOnboardingCompletedFlag(returnTo) : returnTo
    return <Navigate replace to={destination} />
  }

  const trainingHandoffAllowed =
    isTrainingRoute &&
    resolution.step === "training" &&
    isGdprAccepted(data.ui) &&
    progress?.current_step === "training"

  if (trainingHandoffAllowed) {
    return <Outlet />
  }

  const nextReturnTo = isTrainingRoute
    ? returnTo
    : safeReturnTo(withoutForceOnboardingFlag(requestedPath), defaultRoute)
  if (isOnboardingTrainingHandoff) {
    return <Navigate replace to={nextReturnTo} />
  }
  return (
    <>
      <OnboardingOverlayContext.Provider value>
        <Outlet />
      </OnboardingOverlayContext.Provider>
      <OnboardingPage defaultRoute={defaultRoute} returnToOverride={nextReturnTo} />
    </>
  )
}
